/**
 * PAYMENT RETURN HANDLER
 * ======================
 *
 * Stripe sends the payer back to `/?payment=success&session_id=...`. This
 * reads that, calls POST /verify-payment, and reloads once the plan is
 * written, which is what makes the new plan visible everywhere in the app.
 *
 * Two things this has to get right, because both mean a charged user on the
 * free plan:
 *
 * 1. The URL is captured at module load, before React renders. Auth resolves
 *    asynchronously, so verification cannot start on the first render; by the
 *    time it can, a navigation may already have rewritten the URL with
 *    pushState and dropped the session id.
 * 2. A failed verification is reported, loudly, and the query string is left
 *    in place so a reload retries it. Silence here is the worst outcome: the
 *    money is gone and nothing says so.
 *
 * Since POST /stripe/webhook exists, this is no longer the only path to an
 * upgrade — it is the fast one. A verification that fails here is still
 * completed by the webhook.
 */

import { useEffect, useRef } from "react";
import { invokeFunction } from "@/integrations/neon/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface VerifyResponse {
  success: boolean;
  subscription?: { plan_type?: string };
}

/**
 * The payment parameters as the page was opened with, read once at module
 * load. Guarded for the non-browser render in tests.
 */
const initialParams: { status: string | null; sessionId: string | null } =
  typeof window === "undefined"
    ? { status: null, sessionId: null }
    : (() => {
        const params = new URLSearchParams(window.location.search);
        return { status: params.get("payment"), sessionId: params.get("session_id") };
      })();

/** Attempts per page load, including the first. */
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

function clearPaymentParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("payment");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export function PaymentHandler() {
  const { user } = useAuth();
  // The effect re-runs whenever `user` changes identity, which happens on
  // every auth refresh. Verifying once per page load is enough, and a second
  // in-flight call would race the first for the same row.
  const startedRef = useRef(false);

  useEffect(() => {
    if (initialParams.status === "cancelled") {
      toast.error("Payment was cancelled.");
      clearPaymentParams();
      return;
    }

    if (initialParams.status !== "success") return;
    if (!initialParams.sessionId) {
      // Reachable if Stripe's success_url is edited to drop the placeholder.
      // The webhook still upgrades the account; say so rather than nothing.
      toast.error(
        "Payment completed, but this page received no session id to confirm it with. " +
          "Reload in a moment — if the plan has not changed, contact support.",
        { duration: 15000 },
      );
      return;
    }
    // Auth has not resolved yet. The effect re-runs when it does.
    if (!user) return;
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    const verify = async (attempt: number): Promise<void> => {
      const { data, error } = await invokeFunction<VerifyResponse>("/verify-payment", {
        body: { sessionId: initialParams.sessionId },
      });
      if (cancelled) return;

      if (!error && data?.success) {
        const planType = data.subscription?.plan_type ?? "premium";
        toast.success(`Payment successful. Welcome to the ${planType} plan.`);
        // Only cleared on success: leaving the parameters in place after a
        // failure is what makes a reload retry rather than lose the session id.
        clearPaymentParams();
        // The plan is read by hooks all over the tree with no shared
        // invalidation, so a reload is the reliable way to show it.
        setTimeout(() => window.location.reload(), 1500);
        return;
      }

      const message = error?.message ?? "Verification did not confirm the payment";
      console.error(`Payment verification attempt ${attempt} failed:`, message);

      if (attempt < MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt));
        if (cancelled) return;
        return verify(attempt + 1);
      }

      toast.error(
        `Your payment went through, but confirming it here failed: ${message} ` +
          "Reload this page in a minute — the plan usually lands on its own. " +
          "If it does not, contact support and quote this page's address.",
        { duration: 30000 },
      );
    };

    verify(1).catch((err: unknown) => {
      console.error("Payment verification error:", err);
      toast.error(
        "Your payment went through, but confirming it here failed. Reload this " +
          "page in a minute, and contact support if the plan has not changed.",
        { duration: 30000 },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return null;
}
